import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { useAuth } from "../AuthContext";

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const mainLinks = [
    { to: "/", label: "Home" },
    { to: "/onboarding", label: "Onboarding" },
    { to: "/pricing", label: "Pricing" },
    { to: "/contact", label: "Contact" },
  ];

  const moreLinks = [
    { to: "/features", label: "Features" },
    { to: "/careers", label: "Careers" },
    { to: "/tutorials", label: "Tutorials" },
    { to: "/about", label: "About" },
    { to: "/team", label: "Team" },
  ];

  // Track scroll for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [open]);

  const handleLogout = async () => {
    try { await logout(); navigate("/", { replace: true }); } catch (err) {}
  };

  const isActive = (to) => to === location.pathname;

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 20 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-200/50"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-[68px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" onClick={() => window.scrollTo(0, 0)}>
            <motion.img
              whileHover={{ rotate: [0, -5, 5, 0], scale: 1.05 }}
              transition={{ duration: 0.4 }}
              src="/images/trivanalogo.jpg"
              alt="Trivana Logo"
              className="h-9 w-9 sm:h-10 sm:w-10 object-contain rounded-lg shadow-sm"
            />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" style={{ fontFamily: "Montserrat, sans-serif" }}>
              Trivana Hospitality
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {mainLinks.map((l) => (
              <Link key={l.to} to={l.to} className="relative px-3 py-2 group">
                <span
                  className={`relative z-10 text-sm font-medium transition-colors duration-200 ${
                    isActive(l.to) ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"
                  }`}
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {l.label}
                </span>
                {/* Hover pill background */}
                <motion.span
                  className="absolute inset-0 rounded-lg bg-blue-50"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  style={{ zIndex: 0 }}
                />
                {/* Active indicator */}
                {isActive(l.to) && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-blue-600 rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            ))}

            {/* More Menu Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setShowMoreMenu(true)}
              onMouseLeave={() => setShowMoreMenu(false)}
            >
              <button className="flex items-center gap-1 px-3 py-2 relative text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200" style={{ fontFamily: "Montserrat, sans-serif" }}>
                More <FaChevronDown size={10} className={`transition-transform duration-200 ${showMoreMenu ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50"
                  >
                    {moreLinks.map((ml) => (
                      <Link
                        key={ml.to}
                        to={ml.to}
                        onClick={() => setShowMoreMenu(false)}
                        className={`block px-4 py-2.5 text-sm transition-colors ${isActive(ml.to) ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"}`}
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        {ml.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-2 ml-3 pl-3 border-l border-gray-200">
              {!user ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate("/login")}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-blue-600 rounded-lg hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    Login
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 15px rgba(66, 153, 245, 0.3)" }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate("/login?mode=signup")}
                    className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    Sign up
                  </motion.button>
                </>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu((v) => !v)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                      {(user.ownerName || user.email || "?")[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 max-w-[120px] truncate" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {user.ownerName || user.email}
                    </span>
                    <FaChevronDown size={10} className="text-gray-400" />
                  </button>
                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-800 truncate">{user.ownerName}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <button
                          onClick={() => { navigate("/profile"); setShowUserMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          My Profile & Outlets
                        </button>
                        <button
                          onClick={() => { navigate("/onboarding"); setShowUserMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          Continue Onboarding
                        </button>
                        <button
                          onClick={() => { navigate("/admin"); setShowUserMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          Admin Portal
                        </button>
                        <button
                          onClick={() => { logout(); navigate("/"); setShowUserMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                        >
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Hamburger */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {open ? <FaTimes size={22} /> : <FaBars size={22} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] lg:hidden"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-16 left-0 right-0 z-[70] lg:hidden bg-white/95 backdrop-blur-xl shadow-2xl border-t border-gray-100 max-h-[calc(100vh-4rem)] overflow-y-auto rounded-b-2xl"
            >
              <div className="px-4 py-4 space-y-1">
                {mainLinks.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <Link
                      to={l.to}
                      onClick={() => { setOpen(false); window.scrollTo(0, 0); }}
                      className={`block px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                        isActive(l.to)
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                      }`}
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile More Menu */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: mainLinks.length * 0.04 }}
                >
                  <button
                    onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-all"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    More
                    <FaChevronDown size={12} className={`transition-transform duration-200 ${mobileMoreOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileMoreOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pr-2 py-2 space-y-1 border-l-2 border-gray-100 ml-4 mt-1 mb-2">
                          {moreLinks.map((ml) => (
                            <Link
                              key={ml.to}
                              to={ml.to}
                              onClick={() => { setOpen(false); window.scrollTo(0, 0); }}
                              className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive(ml.to) ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"}`}
                              style={{ fontFamily: "Montserrat, sans-serif" }}
                            >
                              {ml.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                <div className="pt-3 mt-2 border-t border-gray-100 space-y-2">
                  {!user ? (
                    <>
                      <button
                        onClick={() => { navigate("/login"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Login
                      </button>
                      <button
                        onClick={() => { navigate("/login?mode=signup"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-md transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="px-4 py-2 bg-gray-50 rounded-xl">
                        <p className="text-sm font-semibold text-gray-800 truncate">{user.ownerName || user.email}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => { navigate("/profile"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        My Profile & Outlets
                      </button>
                      <button
                        onClick={() => { navigate("/onboarding"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Continue Onboarding
                      </button>
                      <button
                        onClick={() => { navigate("/admin"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Admin Portal
                      </button>
                      <button
                        onClick={() => { logout(); navigate("/"); setOpen(false); }}
                        className="block w-full text-center px-4 py-3 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 transition"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Sign Out
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Login Modal — only for other site pages, not for auth actions */}
      {/* (now handled by /login page) */}
    </motion.header>
  );
}

export default Header;
